import { Card } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { IndianRupee, ArrowUpRight, RotateCcw, Wallet } from "lucide-react";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function KpiCard({ icon: Icon, label, value, sub, tone = "sky", testId }) {
  const tones = {
    sky: { bg: "bg-sky-100", fg: "text-sky-600", val: "text-slate-900" },
    emerald: { bg: "bg-emerald-100", fg: "text-emerald-600", val: "text-emerald-700" },
    amber: { bg: "bg-amber-100", fg: "text-amber-600", val: "text-slate-900" },
    rose: { bg: "bg-rose-100", fg: "text-rose-600", val: "text-rose-700" },
  };
  const t = tones[tone] || tones.sky;
  return (
    <Card className="medical-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <p className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">{label}</p>
        <div className={`w-8 h-8 rounded-lg ${t.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${t.fg}`} />
        </div>
      </div>
      <p className={`font-heading text-3xl font-bold mt-3 ${t.val}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1.5">{sub}</p>}
    </Card>
  );
}

export default function RevenueTab({ data }) {
  const k = data?.kpi || {};
  const daywise = data?.daywise || [];
  const weekly = data?.weekly_split || [];
  const onlinePct = k.gross_revenue ? Math.round((k.online_revenue / k.gross_revenue) * 100) : 0;
  return (
    <div className="space-y-6" data-testid="revenue-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-gross" icon={IndianRupee} tone="sky" label="Gross Revenue" value={fmt(k.gross_revenue)} sub={`${k.completed_count || 0} completed`} />
        <KpiCard testId="kpi-online-counter" icon={ArrowUpRight} tone="emerald" label="Online vs Counter"
          value={`${onlinePct}% online`}
          sub={`${fmt(k.online_revenue)} UPI · ${fmt(k.counter_revenue)} cash`} />
        <KpiCard testId="kpi-refunds" icon={RotateCcw} tone="rose" label="Refunds & Cancels"
          value={fmt(k.refunds)} sub={`${k.cancellations || 0} cancelled`} />
        <KpiCard testId="kpi-net" icon={Wallet} tone="emerald" label="Net Revenue"
          value={fmt(k.net_revenue)} sub={`− ${fmt(k.commission)} platform fee`} />
      </div>

      <Card className="medical-card p-5" data-testid="chart-daywise">
        <div className="mb-3">
          <h4 className="font-heading text-base font-semibold text-slate-900">Day-wise Revenue (last 30 days)</h4>
          <p className="text-xs text-slate-500">Completed consultations only</p>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={daywise} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${v/1000}k` : v} />
              <Tooltip formatter={(v) => fmt(v)} labelClassName="text-slate-500" />
              <Line type="monotone" dataKey="revenue" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="medical-card p-5" data-testid="chart-weekly-split">
        <div className="mb-3">
          <h4 className="font-heading text-base font-semibold text-slate-900">Online UPI vs Counter Cash — weekly split</h4>
          <p className="text-xs text-slate-500">Last 8 weeks</p>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weekly} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${v/1000}k` : v} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="online" stackId="a" fill="#10b981" name="Online UPI" radius={[4, 4, 0, 0]} />
              <Bar dataKey="counter" stackId="a" fill="#0ea5e9" name="Walk-in Cash" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
